// Tipuri comune client ↔ server. Sursa unică de adevăr pentru forma datelor.

export const DESTINATIONS = ['pastrez', 'imprumut', 'copii', 'vinted', 'biblioteca_publica', 'donatie'] as const;
export type Destination = typeof DESTINATIONS[number];

export const COPY_STATUSES = ['acasa', 'imprumutata', 'vanduta', 'donata', 'returnata_bibliotecii', 'pierduta'] as const;
export type CopyStatus = typeof COPY_STATUSES[number];

export const READING_STATUSES = ['necitit', 'vreau', 'in_curs', 'citit', 'abandonat'] as const;
export type ReadingStatus = typeof READING_STATUSES[number];

export const ROLES = ['admin', 'adult', 'child'] as const;
export type Role = typeof ROLES[number];

export const CATEGORIES = [
  'Beletristică', 'Clasici', 'Dezvoltare personală', 'Psihologie', 'Parenting', 'Copii',
  'Istorie', 'Biografii & Memorii', 'Știință', 'Sănătate', 'Finanțe & Business', 'Trading',
  'Religie & Spiritualitate', 'Filosofie', 'Poezie', 'Artă', 'Călătorii', 'Gastronomie', 'Diverse'
] as const;

interface Row {
  id: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  version?: number;
  seq?: number;
}

export interface Household extends Row { name: string }

export interface Member extends Row {
  name: string;
  email?: string | null;
  role: Role;
  household_id?: string | null;
  avatar?: string | null;
  color?: string | null;
  reading_goal?: number | null;
}

export interface Work extends Row {
  title: string;
  authors: string;
  isbn?: string | null;
  publisher?: string | null;
  year?: number | null;
  language?: string | null;
  category?: string | null;
  synopsis?: string | null;
  cover_url?: string | null;
  cover_source?: 'auto' | 'verified' | 'photo' | 'manual' | 'legacy' | null;
  tags?: string | null;
  created_by?: string | null;
}

export interface Copy extends Row {
  work_id: string;
  owner_id?: string | null;
  household_id?: string | null;
  shelf?: string | null;
  box?: string | null;
  condition?: string | null;
  destination: Destination;
  age_group?: string | null;
  status: CopyStatus;
  due_date?: string | null;
  notes?: string | null;
  added_at?: string | null;
}

export interface Reading extends Row {
  member_id: string;
  work_id: string;
  status: ReadingStatus;
  started_at?: string | null;
  finished_at?: string | null;
  read_year?: number | null;
  rating?: number | null;
  notes?: string | null;
}

export interface Loan extends Row {
  copy_id: string;
  from_id?: string | null;
  to_id?: string | null;
  to_name?: string | null;
  status: 'cerut' | 'aprobat' | 'refuzat' | 'returnat' | 'anulat';
  note?: string | null;
  requested_at?: string | null;
  approved_at?: string | null;
  returned_at?: string | null;
  due_date?: string | null;
}

export type EntityName = 'households' | 'members' | 'works' | 'copies' | 'reading' | 'loans';

export interface EntityMap {
  households: Household;
  members: Member;
  works: Work;
  copies: Copy;
  reading: Reading;
  loans: Loan;
}

// ---------- Operații (singurul mod în care clientul modifică date) ----------
export type Op =
  | { op_id: string; type: 'create'; entity: EntityName; id: string; fields: Record<string, unknown> }
  | { op_id: string; type: 'update'; entity: EntityName; id: string; fields: Record<string, unknown> }
  | { op_id: string; type: 'delete'; entity: EntityName; id: string }
  | { op_id: string; type: 'reading.set'; work_id: string; member_id?: string; fields: Partial<Reading> };

export interface OpResult {
  op_id: string;
  ok: boolean;
  error?: string;
  duplicate?: boolean;
}

export interface ChangesResponse {
  cursor: number;
  full: boolean;
  me: Member;
  rows: { [K in EntityName]?: EntityMap[K][] };
}

// Câmpurile pe care clientul are voie să le scrie, per entitate (restul le gestionează serverul)
export const WRITABLE_FIELDS: { [K in EntityName]: string[] } = {
  households: ['name'],
  members: ['name', 'email', 'role', 'household_id', 'avatar', 'color', 'reading_goal'],
  works: ['title', 'authors', 'isbn', 'publisher', 'year', 'language', 'category', 'synopsis', 'cover_url', 'cover_source', 'tags'],
  copies: ['work_id', 'owner_id', 'household_id', 'shelf', 'box', 'condition', 'destination', 'age_group', 'status', 'due_date', 'notes', 'added_at'],
  reading: ['status', 'started_at', 'finished_at', 'read_year', 'rating', 'notes'],
  loans: ['copy_id', 'from_id', 'to_id', 'to_name', 'status', 'note', 'requested_at', 'approved_at', 'returned_at', 'due_date']
};

export const DESTINATION_LABELS: Record<Destination, string> = {
  pastrez: 'Păstrez',
  imprumut: 'Se poate împrumuta',
  copii: 'Pentru copii',
  vinted: 'De vândut (Vinted)',
  biblioteca_publica: 'Biblioteca Orașului',
  donatie: 'De donat'
};

export const READING_LABELS: Record<ReadingStatus, string> = {
  necitit: 'Necitită',
  vreau: 'Vreau s-o citesc',
  in_curs: 'Pe noptieră',
  citit: 'Citită',
  abandonat: 'Abandonată'
};
